/* oxlint-disable no-underscore-dangle*/
/**
 * Config resolved at RUNTIME (build-once → promote dev→staging→prod strategy).
 *
 * - Web/PWA: the container injects `window.__APP_CONFIG__` via `/config.js`
 *   (generated by nginx's entrypoint from environment variables at startup). One
 *   image serves every environment; only the container env changes.
 * - Tauri (desktop/mobile): there is no `/config.js`; it falls back to values baked
 *   into `import.meta.env.VITE_*` (build-time) → behavior unchanged.
 * - Local dev: likewise (import.meta.env from apps/web/.env) or a localhost fallback.
 */
interface AppRuntimeConfig {
  SERVER_URL?: string;
  POWERSYNC_URL?: string;
  WEB_URL?: string;
}

const runtime: AppRuntimeConfig =
  (globalThis as typeof globalThis & { __APP_CONFIG__?: AppRuntimeConfig }).__APP_CONFIG__ ?? {};

/**
 * First value actually present. A bare `??` is not enough: the entrypoint writes
 * `"CLAVE": ""` for every env the container does not define, and an empty string is
 * not nullish → it would win over the default and leave, for example, an empty
 * base URL. Here "unset" and "empty" are the same.
 */
function firstConfigured(...values: (string | undefined)[]): string | undefined {
  return values.find((v) => v !== undefined && v.trim() !== "")?.trim();
}

/** Backend base (auth/api/server).*/
export const SERVER_URL: string =
  firstConfigured(
    runtime.SERVER_URL,
    import.meta.env.VITE_SERVER_URL,
    import.meta.env.VITE_API_URL,
  ) ?? "http://localhost:3333";

/** PowerSync service endpoint.*/
export const POWERSYNC_URL: string =
  firstConfigured(runtime.POWERSYNC_URL, import.meta.env.VITE_POWERSYNC_URL) ??
  "http://localhost:8085";

/**
 * PUBLIC web origin (where `privacy.html`, `terms.html` and
 * `delete-account.html` live).
 *
 * On the web it matches the origin itself, so legal pages link with a relative
 * path. In the native binary this does not exist: the origin is `tauri://localhost`,
 * and the link must be absolute to open in the system browser. See
 * `legal-links.ts`.
 */
export const WEB_URL: string =
  firstConfigured(runtime.WEB_URL, import.meta.env.VITE_WEB_URL) ?? "https://app.cashsync.app";
